import { useState } from "preact/compat"
import { differenceInDays, parseISO } from "date-fns"
import {
  Integration,
  IntegrationWithJobInfo,
  KeheConfig,
  KeheUdrDeduction,
  KeheUdrStatus,
  UnfiConfig,
  findEmailIntegrationForUsername,
  updateIntegration,
} from "@/api/integration"
import { ConnectMailboxButtons } from "@/connect_mailbox/connect_mailbox_buttons"
import { api_fetch } from "@/api/client"
import { EmailSelector } from "@/setup/config/email-config-page"
import { Input } from "@/components/form/Input"
import { Button } from "@/components/ui/button"
import { toast } from "@/components/ui/use-toast"
import { useAsyncEffect } from "@/utils"
import { SyncStatusDisplay } from "../sync-status-row"
import gmailLogo from "/img/integration/gmail.png"
import microsoftLogo from "/img/integration/microsoft.png"
import { UdrDeductionsTable } from "./udr-deductions-table"

const UDR_REQUEST_COOLDOWN_DAYS = 30

interface KeheUdrSettingsProps {
  integration: IntegrationWithJobInfo
  emailAccounts: IntegrationWithJobInfo[]
  unfiIntegration: Integration | null
}

function getKeheUdrDefaults(keheData: KeheConfig, unfiIntegration: Integration | null) {
  const unfiData = unfiIntegration?.data as UnfiConfig | undefined
  const email =
    keheData.email_address?.trim() ||
    unfiData?.email_address?.trim() ||
    ""
  const contactName =
    keheData.dispute_from_full_name?.trim() ||
    unfiData?.dispute_from_full_name?.trim() ||
    ""
  return { email, contactName }
}

function formatDate(dateString: string | null) {
  if (!dateString) {
    return "—"
  }
  try {
    return new Date(dateString).toLocaleDateString()
  } catch {
    return "—"
  }
}

function getUdrStatusDisplay(status: KeheUdrStatus) {
  if (status.receiving_udr) {
    return { color: "green", text: "Receiving UDR notifications" }
  }
  if (status.last_request_sent_at) {
    return { color: "yellow", text: "Setup request sent — waiting for UDR emails" }
  }
  return { color: "red", text: "Not receiving UDR notifications" }
}

export function KeheUdrSettings({ integration, emailAccounts, unfiIntegration }: KeheUdrSettingsProps) {
  const keheData = integration.data as unknown as KeheConfig
  const defaults = getKeheUdrDefaults(keheData, unfiIntegration)

  const [status, setStatus] = useState<KeheUdrStatus | null>(null)
  const [deductions, setDeductions] = useState<KeheUdrDeduction[]>([])
  const [selectedEmail, setSelectedEmail] = useState(defaults.email)
  const [contactName, setContactName] = useState(defaults.contactName)
  const [loading, setLoading] = useState(true)
  const [savingConfig, setSavingConfig] = useState(false)
  const [requesting, setRequesting] = useState(false)

  const emailIntegration = selectedEmail
    ? findEmailIntegrationForUsername(emailAccounts, selectedEmail)
    : null

  useAsyncEffect(async () => {
    const [statusRes, deductionsRes] = await Promise.all([
      api_fetch<KeheUdrStatus>("/integration/kehe/udr/status"),
      api_fetch<KeheUdrDeduction[]>("/integration/kehe/udr/deductions"),
    ])
    if (statusRes.ok) {
      setStatus(statusRes.value.data)
      if (statusRes.value.data.email_address) {
        setSelectedEmail(statusRes.value.data.email_address)
      }
      if (statusRes.value.data.dispute_from_full_name) {
        setContactName(statusRes.value.data.dispute_from_full_name)
      }
    }
    if (deductionsRes.ok) {
      setDeductions(deductionsRes.value.data)
    }
    setLoading(false)
  }, [])

  async function saveUdrConfig() {
    setSavingConfig(true)
    const config: KeheConfig = {
      ...(integration.data as unknown as KeheConfig),
      email_address: selectedEmail.trim() || null,
      dispute_from_full_name: contactName.trim() || null,
    }
    const res = await updateIntegration(integration.id, { data: config })
    setSavingConfig(false)
    if (!res.ok) {
      toast({
        title: "Save failed",
        description: "Could not save UDR notification settings.",
        variant: "destructive",
      })
      return
    }
    toast({
      title: "Settings saved",
      description: "UDR notification mailbox settings updated.",
    })
    const statusRes = await api_fetch<KeheUdrStatus>("/integration/kehe/udr/status")
    if (statusRes.ok) {
      setStatus(statusRes.value.data)
    }
  }

  async function handleRequestUdrNotifications() {
    setRequesting(true)
    const res = await api_fetch<{ email_sent: boolean; message: string }>("/integration/kehe/udr/request", {
      method: "POST",
      body: {},
    })
    setRequesting(false)
    if (res.ok) {
      toast({
        title: "Request sent",
        description: res.value.data.message,
      })
      const statusRes = await api_fetch<KeheUdrStatus>("/integration/kehe/udr/status")
      if (statusRes.ok) {
        setStatus(statusRes.value.data)
      }
    } else {
      toast({
        title: "Request failed",
        description: res.error.message,
        variant: "destructive",
      })
    }
  }

  function handleDeductionUpdated(updated: KeheUdrDeduction) {
    setDeductions((current: KeheUdrDeduction[]) =>
      current.map((row: KeheUdrDeduction) => (row.id === updated.id ? updated : row)),
    )
  }

  if (loading) {
    return <div class="text-gray-500 text-sm">Loading UDR settings...</div>
  }

  const statusDisplay = status ? getUdrStatusDisplay(status) : null
  const lastRequest = status?.last_request_sent_at ? parseISO(status.last_request_sent_at) : null
  const isRequestRecent = lastRequest ? differenceInDays(new Date(), lastRequest) < UDR_REQUEST_COOLDOWN_DAYS : false
  const canRequest =
    Boolean(selectedEmail.trim()) &&
    Boolean(contactName.trim()) &&
    Boolean(emailIntegration) &&
    !isRequestRecent

  return (
    <div class="space-y-6">
      {status && status.email_integrations_count === 0 ? (
        <div class="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
          <p class="text-sm text-amber-800 font-medium">Connect an email account to receive UDR notifications</p>
          <ConnectMailboxButtons microsoftLabel="Connect Microsoft" />
        </div>
      ) : (
        <>
          <EmailSelector
            emailAccounts={emailAccounts}
            selectedEmail={selectedEmail}
            onSelect={setSelectedEmail}
            label=""
            description="KeHE will send UDR notifications to the selected email address."
          />
          <Input
            label="Contact name for this inbox"
            type="text"
            name="dispute_from_full_name"
            value={contactName}
            onChange={(e) => setContactName(e.currentTarget.value)}
          />
          {emailIntegration ? (
            <div class="space-y-2">
              <div class="flex items-center gap-3">
                <img
                  src={emailIntegration.name === "gmail" ? gmailLogo : microsoftLogo}
                  alt={emailIntegration.name}
                  class="h-6 w-6"
                />
                <span class="text-sm text-gray-700">{emailIntegration.username}</span>
              </div>
              <SyncStatusDisplay
                lastSyncJob={emailIntegration.last_sync_job}
                integrationName={emailIntegration.name}
              />
            </div>
          ) : selectedEmail ? (
            <p class="text-sm text-red-500">No connected email integration found for {selectedEmail}.</p>
          ) : null}
          <Button variant="outline" disabled={savingConfig} onClick={saveUdrConfig}>
            {savingConfig ? "Saving..." : "Save UDR mailbox settings"}
          </Button>
        </>
      )}

      {statusDisplay ? (
        <div class="space-y-2 border-t border-gray-100 pt-4">
          <div class="flex items-center gap-2">
            <div class={`w-3 h-3 rounded-full bg-${statusDisplay.color}-500`} />
            <span class={`text-sm text-${statusDisplay.color}-700`}>{statusDisplay.text}</span>
          </div>
          <div class="text-sm text-gray-600 space-y-1">
            <div>Last UDR received: {formatDate(status?.last_udr_received_at ?? null)}</div>
            <div>Last setup request sent: {formatDate(status?.last_request_sent_at ?? null)}</div>
            {status?.last_request_inbound_at ? (
              <div>Last KeHE reply: {formatDate(status.last_request_inbound_at)}</div>
            ) : null}
          </div>
          <div class="flex items-center gap-2">
            <Button variant="outline" disabled={!canRequest || requesting} onClick={handleRequestUdrNotifications}>
              {requesting ? "Sending request..." : "Request UDR notifications"}
            </Button>
            {isRequestRecent ? (
              <span class="text-xs text-gray-500">
                Available {UDR_REQUEST_COOLDOWN_DAYS} days after last request
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      <div class="border-t border-gray-100 pt-4">
        <h3 class="text-sm font-medium text-gray-900 mb-3">Pending UDR deductions</h3>
        <UdrDeductionsTable deductions={deductions} onUpdated={handleDeductionUpdated} />
      </div>
    </div>
  )
}
